import type {MouseEventHandler, ReactElement} from 'react';
import OverlayTrigger from 'react-bootstrap/OverlayTrigger';
import Tooltip from 'react-bootstrap/Tooltip';
import type {BaseControlledListItem} from '@Components/controlled-list/controlled-list.types';
import {ControlledListItemSize} from '@Components/controlled-list/controlled-list.types';
import {Text, TextSize} from '@Components/text';
import {Icon} from '@Components/icon-v2';
import {IconSize} from '@Components/icon-v2/icon.types';
import {ActivityIndicatorSize, ActivityIndicatorType} from '@Components/activity-indicator/activity-indicator.types';
import {ActivityIndicator} from '@Components/activity-indicator/activity-indicator';
import styles from './controlled-list-item-type-7.module.scss';
import {CircularProgressLoader, LOADER_SIZE} from '@Components/circular-progress-loader';
import {noop} from '@Utils/general.util';

export interface ControlledListItemType7Props extends BaseControlledListItem {
  icon?: string;
  /** Rendered immediately after the text. Distinct from hasPulsatingDot's 16px ActivityIndicator — callers pass their own element. */
  textSuffixElement?: ReactElement;
  iconClasses?: string;
  textClasses?: string;
  color?: ColorTypes;
  shortcut?: string;
  rightIcon?: string;
  rightIconClassName?: string;
  isDisabled?: boolean;
  onRightIconClick?(id?: string): void;
  customElement?: ReactElement<any>;
  customElementClasses?: string;
  hasPulsatingDot?: boolean;
  tooltip?: string;
  'data-testid'?: string;
}

export enum ColorTypes {
  default = 'default',
  danger = 'danger',
  success = 'success',
}

export function ControlledListItemType7({
  id = '',
  className = '',
  isSelected = false,
  icon = '',
  color = ColorTypes.default,
  textClasses = '',
  shortcut = '',
  rightIcon = '',
  rightIconClassName = '',
  isDisabled = false,
  hasPulsatingDot = false,
  onClick = noop,
  listSize = ControlledListItemSize.SMALL,
  ...props
}: ControlledListItemType7Props): ReactElement<any> {
  const getTextColorClass = (): string => {
    if (color === ColorTypes.default) {
      return isSelected ? 'content-primary' : 'content-body';
    }
    return '';
  };

  const getIconSize = (): IconSize => {
    switch (listSize) {
      case ControlledListItemSize.MEDIUM:
        return IconSize.SIZE_ICON_20;
      case ControlledListItemSize.SMALL:
      default:
        return IconSize.SIZE_ICON_16;
    }
  };

  const getTextSize = (): TextSize => {
    switch (listSize) {
      case ControlledListItemSize.MEDIUM:
        return TextSize.SMALL;
      case ControlledListItemSize.SMALL:
      default:
        return TextSize.XSMALL;
    }
  };

  const rightIconClickCallback: MouseEventHandler = (e): void => {
    if (isDisabled) {
      return;
    }

    if (rightIcon && !!props.onRightIconClick) {
      e.stopPropagation();
      props.onRightIconClick(id);
    }
  };

  const listItem = (
    <li
      data-testid={props['data-testid']}
      tabIndex={props.tooltip ? 0 : undefined}
      onClick={(event): void => {
        if (isDisabled) {
          return;
        }

        onClick(id, event);
      }}
      className={`${styles.listItemContainer} ${
        listSize === ControlledListItemSize.SMALL ? styles.small : styles.medium
      } hover-transition-all radius-2 spacing-p-3 flex-row-justify-between ${className} ${isDisabled ? `${styles.disabled} disabled` : ''}`}
    >
      <div className="flex-center">
        <Icon icon={icon} className={`${styles.icon} ${color === ColorTypes.danger ? styles.danger : ''} ${color === ColorTypes.success ? styles.success : ''} spacing-m-r-2 ${props.iconClasses ?? ''}`} size={getIconSize()} />
        <Text className={`${isDisabled ? 'disabled' : ''} ${textClasses} ${getTextColorClass()} ${styles.listText}`} val={props.text} size={getTextSize()} bold={isSelected} />
        {props.textSuffixElement ?? null}
        {hasPulsatingDot ? <ActivityIndicator size={ActivityIndicatorSize.SIZE_16} type={ActivityIndicatorType.NEW_PULSE} className="spacing-m-l-1" /> : null}
      </div>
      {rightIcon ? (
        <Icon onClick={rightIconClickCallback} className={`${shortcut ? 'spacing-m-r-1' : ''} ${rightIconClassName}`} icon={rightIcon} size={IconSize.SIZE_ICON_16} />
      ) : null}
      {shortcut ? <Text className="content-disabled" val={shortcut} size={TextSize.XXSMALL} bold={isSelected} /> : null}
      {props.isLoading ? <CircularProgressLoader size={LOADER_SIZE.XSMALL} /> : null}
      {props.customElement ? <div className={`${styles.customElement} ${props.customElementClasses ?? ''}`}>{props.customElement}</div> : null}
    </li>
  );

  if (!props.tooltip) {
    return listItem;
  }

  return (
    <OverlayTrigger
      trigger={['hover', 'focus']}
      placement="top"
      container={document.body}
      overlay={
        <Tooltip id={`${id}-controlled-list-item-tooltip`} className={`spacing-m-b-1 _rounded-arrow ${styles.tooltip}`}>
          <Text size={TextSize.XSMALL} val={props.tooltip} />
        </Tooltip>
      }
    >
      {listItem}
    </OverlayTrigger>
  );
}
